import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiArrowLeft, FiSearch, FiX } from 'react-icons/fi'; // Using Mail icon as placeholder for envelopes in image
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import PersonSelectionModal from '../Asthapan/PersonSelectionModal';
import PersonDetailsForm from '../Asthapan/PersonDetailsForm';
import AccountSelectionModal from './AccountSelectionModal';
import Namuna7Receipt from './Namuna7Receipt';
import Namuna12Receipt from './Namuna12Receipt';
import TransactionViewModal from './TransactionViewModal';
import ChequeDetailsModal from './ChequeDetailsModal';

const OtherTransactions = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('rokh-jama');
    const [view, setView] = useState('list'); // 'list' or 'form'
    const [viewingTx, setViewingTx] = useState(null);

    //---------------------------------------------------------
    // TAB CONFIGURATION
    //---------------------------------------------------------
    const tabConfig = {
        'rokh-jama': {
            title: 'रोख जमा',
            dbPath: 'Grampanchayat/Panipuravtha/Transactions/RokhJama',
            voucherType: 'जमा व्हॉटचर',
            showPrint: true
        },
        'rokh-nave': {
            title: 'रोख नावे',
            dbPath: 'Grampanchayat/Panipuravtha/Transactions/RokhNave',
            voucherType: 'नावे व्हॉटचर',
            showPrint: false // No print requirement specified yet
        },
        'cheque-jama': {
            title: 'धनादेश जमा',
            dbPath: 'Grampanchayat/Panipuravtha/Transactions/ChequeJama',
            voucherType: 'बँक जमा',
            showPrint: false
        },
        'cheque-nave': {
            title: 'धनादेश नावे',
            dbPath: 'Grampanchayat/Panipuravtha/Transactions/ChequeNave',
            voucherType: 'बँक नावे',
            showPrint: false
        },
        'jv': {
            title: 'जे.व्ही.',
            dbPath: 'Grampanchayat/Panipuravtha/Transactions/JV',
            voucherType: 'जनरल व्हॉटचर',
            showPrint: false
        }
    };

    const currentConfig = tabConfig[activeTab];

    //---------------------------------------------------------
    // DATA STATE
    //---------------------------------------------------------
    const [transactions, setTransactions] = useState([]);
    
    // Form State
    const initialFormState = {
        date: new Date().toISOString().split('T')[0],
        voucherType: currentConfig ? currentConfig.voucherType : '',
        challanNo: '',
        bankName: '',
        chequeNo: '',
        chequeDate: '',
        chequeBankName: '',
        chequeNarration: '',
        personName: '',
        resolutionNo: '',
        resolutionDate: '',
        bankLedgerId: '',
        bankLedgerName: '',
        transactions: [], // Array of { accountId, accountName, amount, narration }
        totalAmount: 0
    };
    const [formData, setFormData] = useState(initialFormState);
    
    // Transaction Row State for Form
    const [currentRow, setCurrentRow] = useState({
        account: null,
        amount: '',
        creditAmount: '',
        debitAmount: '',
        narration: ''
    });

    const [showAccountModal, setShowAccountModal] = useState(false);

    //---------------------------------------------------------
    // FETCH DATA
    //---------------------------------------------------------
    useEffect(() => {
        if (!currentConfig) return;

        setFormData(prev => ({ ...prev, voucherType: currentConfig.voucherType }));
        setTransactions([]); // Clear previous data

        const txRef = ref(database, currentConfig.dbPath);
        const unsubscribeTx = onValue(txRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadedTx = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // Sort by date desc
                setTransactions(loadedTx.reverse());
            } else {
                setTransactions([]);
            }
        });

        return () => {
            unsubscribeTx();
        };
    }, [activeTab]); // Re-run when tab changes

    //---------------------------------------------------------
    // HANDLERS
    //---------------------------------------------------------
    const handleAddRow = () => {
        if (!currentRow.account) {
            alert("खाते निवडा");
            return;
        }
        
        // For JV, check if at least one amount is entered
        if (activeTab === 'jv') {
             if (!currentRow.creditAmount && !currentRow.debitAmount) {
                alert("जमा किंवा नावे रक्कम भरा");
                return;
             }
        } else {
             if (!currentRow.amount) {
                alert("रक्कम निवडा");
                return;
             }
        }

        const newRow = {
            id: Date.now(),
            accountId: currentRow.account.id,
            accountName: currentRow.account.accountName,
            amount: activeTab === 'jv' ? 0 : parseFloat(currentRow.amount),
            creditAmount: activeTab === 'jv' ? (parseFloat(currentRow.creditAmount) || 0) : 0,
            debitAmount: activeTab === 'jv' ? (parseFloat(currentRow.debitAmount) || 0) : 0,
            narration: currentRow.narration
        };

        const updatedTx = [...formData.transactions, newRow];
        
        // Calculate total based on context. For JV, maybe total credits? or separate totals?
        // Using total amount for standard tabs
        let newTotal = 0;
        if (activeTab !== 'jv') {
             newTotal = updatedTx.reduce((sum, item) => sum + item.amount, 0);
        } else {
             // For JV, we might want to track total Credit and Total Debit separately if needed
             // For now just keeping totalAmount as sum of amounts (which is 0 for JV in simple logic, or maybe sum of credits?)
             // Let's leave totalAmount as 0 for JV or handle it if needed
             newTotal = 0; 
        }

        setFormData({
            ...formData,
            transactions: updatedTx,
            totalAmount: newTotal
        });

        setCurrentRow({ account: null, amount: '', creditAmount: '', debitAmount: '', narration: '' });
    };

    const handleRemoveRow = (rowId) => {
        const updatedTx = formData.transactions.filter(t => t.id !== rowId);
        const newTotal = updatedTx.reduce((sum, item) => sum + item.amount, 0);
        setFormData({
            ...formData,
            transactions: updatedTx,
            totalAmount: newTotal
        });
    };

    const handleSave = () => {
        if (!formData.personName) {
            alert("व्यक्तीचे नाव भरा");
            return;
        }
        if (formData.transactions.length === 0) {
            alert("किमान एक व्यवहार जोडा");
            return;
        }

        const newRecord = {
            ...formData,
            createdAt: Date.now()
        };

        push(ref(database, currentConfig.dbPath), newRecord)
            .then(() => {
                alert("Data Saved Successfully!");
                setFormData({...initialFormState, voucherType: currentConfig.voucherType});
                setView('list');
            })
            .catch(err => alert("Error: " + err.message));
    };

    const handleDelete = (id) => {
        if (window.confirm("खरोखर खोडायचे आहे का?")) {
            remove(ref(database, `${currentConfig.dbPath}/${id}`))
                .then(() => alert("Deleted successfully"));
        }
    };

    //---------------------------------------------------------
    // PERSON SELECTION LOGIC
    //---------------------------------------------------------
    const [showPersonModal, setShowPersonModal] = useState(false);
    const [showPersonForm, setShowPersonForm] = useState(false);

    const handlePersonSelect = (person) => {
        setFormData(prev => ({ ...prev, personName: person.fullName }));
        setShowPersonModal(false);
    };

    const handleSaveNewPerson = (personData) => {
        const fullPersonData = {
            ...personData,
            fullName: `${personData.firstName} ${personData.middleName || ''} ${personData.lastName}`.replace(/\s+/g, ' ').trim(),
            createdAt: new Date().toISOString()
        };

        const personRef = ref(database, 'Grampanchayat/Gramniddhi/Certificates and licenses/PersonList');
        push(personRef, fullPersonData)
            .then((snap) => {
                alert("New Person Added Successfully!");
                // Auto select the new person
                handlePersonSelect({ ...fullPersonData, id: snap.key });
                setShowPersonForm(false);
            })
            .catch(err => alert("Error saving person: " + err.message));
    };

    //---------------------------------------------------------
    // CHEQUE DETAILS LOGIC
    //---------------------------------------------------------
    const [showChequeDetailsModal, setShowChequeDetailsModal] = useState(false);

    const handleSaveChequeDetails = (details) => {
        setFormData(prev => ({
            ...prev,
            chequeNo: details.chequeNo,
            chequeDate: details.chequeDate,
            chequeBankName: details.bankName,
            chequeNarration: details.narration
        }));
    };

    const sidebarItems = [
        { id: 'rokh-jama', label: 'रोख जमा' },
        { id: 'rokh-nave', label: 'रोख नावे' },
        { id: 'cheque-jama', label: 'धनादेश जमा' },
        { id: 'cheque-nave', label: 'धनादेश नावे' },
        { id: 'jv', label: 'जे व्ही' },
    ];

    //---------------------------------------------------------
    // PRINT LOGIC
    //---------------------------------------------------------
    const [printingTx, setPrintingTx] = useState(null);

    if (printingTx) {
        if (activeTab === 'rokh-nave') {
            return (
                <Namuna12Receipt 
                    transaction={printingTx} 
                    onPrintComplete={() => setPrintingTx(null)} 
                />
            );
        }
        return (
            <Namuna7Receipt 
                transaction={printingTx} 
                onPrintComplete={() => setPrintingTx(null)} 
            />
        );
    }

    return (
        <div className="flex min-h-screen bg-gray-100 font-poppins">
            {/* SIDEBAR */}
            <div className="w-64 bg-gray-800 text-white flex flex-col">
                <div className="p-4 border-b border-gray-700 flex items-center gap-2">
                     <button onClick={() => navigate(-1)} className="hover:bg-gray-700 p-1 rounded-full">
                        <FiArrowLeft />
                     </button>
                     <span className="font-bold">इतर व्यवहार</span>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {sidebarItems.map(item => (
                        <div 
                            key={item.id}
                            onClick={() => { setActiveTab(item.id); setView('list'); }}
                            className={`p-4 border-b border-gray-700 cursor-pointer hover:bg-gray-700 flex flex-col items-center gap-2 ${activeTab === item.id ? 'bg-gray-700 border-l-4 border-l-blue-500' : ''}`}
                        >
                            <FiMail className="w-8 h-8" />
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="flex-1 p-6">
                
                {/* RENDER CONTENT FOR ALL TABS IF CONFIG EXISTS */}
                {currentConfig ? (
                    <div className="bg-white rounded shadow border border-blue-900 overflow-hidden">
                        {/* HEADER */}
                        <div className={`${(activeTab === 'rokh-nave' || activeTab === 'cheque-nave') ? 'bg-red-600' : activeTab === 'jv' ? 'bg-orange-500' : 'bg-blue-800'} text-white p-3 flex justify-between items-center`}>
                            <h2 className="text-xl">{currentConfig.title}</h2>
                            {view === 'list' && (
                                <button 
                                    onClick={() => setView('form')}
                                    className="bg-green-600 hover:bg-green-700 px-4 py-1 rounded shadow"
                                >
                                    नविन
                                </button>
                            )}
                            {view === 'form' && (
                                <button 
                                    onClick={() => setView('list')}
                                    className="bg-gray-600 hover:bg-gray-700 px-4 py-1 rounded shadow"
                                >
                                    यादी पहा
                                </button>
                            )}
                        </div>

                        {/* LIST VIEW */}
                        {view === 'list' && (
                            <div>
                                <div className="p-4 bg-gray-50 flex items-center gap-4 border-b">
                                    <div className="flex items-center gap-2">
                                        <label>दिनांक</label>
                                        <input type="date" className="border p-1 rounded" defaultValue="2025-04-01" />
                                        <button className="bg-blue-800 text-white px-4 py-1 rounded">दाखवा</button>
                                    </div>
                                </div>
                                <table className="w-full text-left border-collapse">
                                    <thead className={`text-white ${activeTab === 'jv' ? 'bg-orange-500' : 'bg-blue-800'}`}>
                                        {activeTab === 'cheque-nave' ? (
                                            <tr>
                                                <th className="p-2 border w-12 text-center">#</th>
                                                <th className="p-2 border">खातावणी निवडा</th>
                                                <th className="p-2 border text-right">नावे रक्कम</th>
                                                <th className="p-2 border text-right">वाटणीची रक्कम</th>
                                                <th className="p-2 border text-right">शिल्लक</th>
                                            </tr>
                                        ) : activeTab === 'jv' ? (
                                            <tr>
                                                <th className="p-2 border w-12 text-center">#</th>
                                                <th className="p-2 border">खातावणी निवडा</th>
                                                <th className="p-2 border text-right">जमा रक्कम</th>
                                                <th className="p-2 border text-right">नावे रक्कम</th>
                                                <th className="p-2 border text-right">वाटणीची रक्कम</th>
                                                <th className="p-2 border text-right">शिल्लक</th>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th className="p-2 border text-center font-bold">#</th>
                                                <th className="p-2 border">खाते (लेजर)</th>
                                                <th className="p-2 border text-right">रक्कम</th>
                                                <th className="p-2 border">शेरा</th>
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody>
                                        {transactions.map((tx, idx) => (
                                            <tr key={tx.id} className="border-b hover:bg-gray-50">
                                                <td className="p-2 border-r">{tx.date}</td>
                                                <td className="p-2 border-r">1</td> {/* TODO: Auto increment voucher no */}
                                                <td className="p-2 border-r">{tx.personName}</td>
                                                <td className="p-2 border-r text-right">{tx.totalAmount}</td>
                                                <td className="p-2 border-r">-</td>
                                                <td className="p-2 flex justify-center gap-2">
                                                    {currentConfig.showPrint && activeTab !== 'rokh-nave' && (
                                                        <button 
                                                            onClick={() => setPrintingTx(tx)}
                                                            className="bg-blue-800 text-white px-2 py-1 rounded text-xs"
                                                        >
                                                            नमुना नं. ७
                                                        </button>
                                                    )}
                                                    {activeTab === 'rokh-nave' && (
                                                         <button 
                                                            onClick={() => setPrintingTx(tx)}
                                                            className="bg-blue-800 text-white px-2 py-1 rounded text-xs"
                                                        >
                                                            नमुना नं. 12
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => setViewingTx(tx)}
                                                        className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                                                    >
                                                        पाहाणे
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(tx.id)}
                                                        className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                                                    >
                                                        खोडणे
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {transactions.length === 0 && (
                                            <tr><td colSpan="6" className="p-4 text-center text-gray-500">No records found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                <div className="bg-blue-800 p-2 flex justify-center gap-1">
                                    {/* Pagination Mock */}
                                    <button className="bg-blue-600 text-white px-2 rounded">1</button>
                                </div>
                            </div>
                        )}

                        {/* FORM VIEW */}
                        {view === 'form' && (
                            <div className="p-4">
                                {/* ROKH NAVE SPECIFIC FIELDS */}
                                {activeTab === 'rokh-nave' ? (
                                    <>
                                        <div className="grid grid-cols-3 gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label>दिनांक</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                            <div className=" bg-green-600 text-white p-1 px-3 rounded text-center">
                                                {formData.voucherType}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label>चलन क्र.</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.challanNo}
                                                    onChange={(e) => setFormData({...formData, challanNo: e.target.value})}
                                                    placeholder="चलन क्र. भरा" 
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 mb-4 bg-gray-100 p-2 rounded">
                                            <label className="w-32">व्यक्तीचे नाव :</label>
                                            <div className="flex-1 flex gap-2">
                                                <input 
                                                    type="text" 
                                                    value={formData.personName}
                                                    onChange={(e) => setFormData({...formData, personName: e.target.value})}
                                                    placeholder="व्यक्तीचे नाव" 
                                                    className="border p-2 rounded w-full" 
                                                />
                                                <button 
                                                    onClick={() => setShowPersonModal(true)}
                                                    className="bg-blue-800 text-white px-3 rounded"
                                                >
                                                    ...
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label className="w-32">resolutionNo :</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.resolutionNo}
                                                    onChange={(e) => setFormData({...formData, resolutionNo: e.target.value})}
                                                    placeholder="resolution No भरा." 
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="w-32">ठराव दिनांक:</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.resolutionDate}
                                                    onChange={(e) => setFormData({...formData, resolutionDate: e.target.value})}
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : activeTab === 'cheque-jama' ? (
                                    <>
                                        {/* CHEQUE JAMA SPECIFIC LAYOUT */}
                                         <div className="grid grid-cols-4 gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label>दिनांक</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                            <div className="col-span-1 bg-green-600 text-white p-1 px-3 rounded text-center">
                                                {formData.voucherType}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label>चलन क्र.</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.challanNo}
                                                    onChange={(e) => setFormData({...formData, challanNo: e.target.value})}
                                                    placeholder="चलन क्र. भरा" 
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 mb-4 bg-gray-100 p-2 rounded">
                                            <label className="w-32">व्यक्तीचे नाव :</label>
                                            <div className="flex-1 flex gap-2">
                                                <input 
                                                    type="text" 
                                                    value={formData.personName}
                                                    onChange={(e) => setFormData({...formData, personName: e.target.value})}
                                                    placeholder="व्यक्तीचे नाव" 
                                                    className="border p-2 rounded w-full" 
                                                />
                                                <button 
                                                    onClick={() => setShowPersonModal(true)}
                                                    className="bg-blue-800 text-white px-3 rounded"
                                                >
                                                    ...
                                                </button>
                                            </div>
                                        </div>

                                        {/* Bank Ledger Selection Row */}
                                        <div className="grid grid-cols-2 gap-4 mb-4 bg-blue-50 p-2 rounded border border-blue-200">
                                            <div className="flex items-center gap-2">
                                                <label className="w-24">खातावणी</label>
                                                <input readOnly className="border p-1 w-16 bg-gray-100" />
                                                <button 
                                                    onClick={() => {
                                                        // Using the generic account modal but storing result specifically for Bank Ledger
                                                        setShowAccountModal('bank'); 
                                                    }}
                                                    className="bg-blue-800 text-white px-3 rounded"
                                                >
                                                    ...
                                                </button>
                                                <input 
                                                    readOnly 
                                                    value={formData.bankLedgerName || ''}
                                                    placeholder="Select Bank Account"
                                                    className="border p-1 flex-1 bg-gray-100" 
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                 <label className="w-24 text-right">धनादेश नं.</label>
                                                 <input 
                                                    value={formData.chequeNo}
                                                    onChange={(e) => setFormData({...formData, chequeNo: e.target.value})}
                                                    className="border p-1 flex-1" 
                                                />
                                                <button className="bg-blue-800 text-white px-3 rounded">...</button>
                                            </div>
                                        </div>
                                    </>
                                ) : activeTab === 'cheque-nave' ? (
                                    <>
                                        {/* CHEQUE NAVE SPECIFIC LAYOUT */}
                                         <div className="flex items-center gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold">दिनांक</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="border p-1 rounded" 
                                                />
                                            </div>
                                            <div className="flex-1 flex justify-center">
                                                <span className="bg-green-600 text-white px-6 py-1 rounded font-bold">
                                                    {formData.voucherType}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold">चलन क्र.</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.challanNo}
                                                    onChange={(e) => setFormData({...formData, challanNo: e.target.value})}
                                                    placeholder="चलन क्र. भरा." 
                                                    className="border p-1 rounded w-32" 
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 mb-4">
                                            <label className="w-32 font-semibold">व्यक्तीचे नाव :</label>
                                            <div className="flex-1 flex gap-2">
                                                <input 
                                                    type="text" 
                                                    value={formData.personName}
                                                    onChange={(e) => setFormData({...formData, personName: e.target.value})}
                                                    placeholder="व्यक्तीचे नाव" 
                                                    className="border p-2 rounded w-full bg-gray-100" 
                                                />
                                                <button 
                                                    onClick={() => setShowPersonModal(true)}
                                                    className="bg-blue-800 text-white px-3 rounded"
                                                >
                                                    ...
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="flex items-center gap-2 flex-1">
                                                <label className="w-32 font-semibold">resolutionNo :</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.resolutionNo}
                                                    onChange={(e) => setFormData({...formData, resolutionNo: e.target.value})}
                                                    placeholder="resolution No भरा." 
                                                    className="border p-2 rounded flex-1" 
                                                />
                                            </div>
                                            <div className="flex items-center gap-2 flex-1 justify-end">
                                                <label className="font-semibold">ठराव दिनांक:</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.resolutionDate}
                                                    onChange={(e) => setFormData({...formData, resolutionDate: e.target.value})}
                                                    className="border p-2 rounded" 
                                                />
                                            </div>
                                        </div>

                                    </>
                                ) : activeTab === 'jv' ? (
                                    <>
                                        {/* JV SPECIFIC LAYOUT */}
                                         <div className="flex items-center gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold">दिनांक</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="border p-1 rounded" 
                                                />
                                            </div>
                                            <div className="flex-1 flex justify-center">
                                                <span className="bg-orange-500 text-white px-8 py-1 rounded-sm font-bold w-1/2 text-center">
                                                    {formData.voucherType}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold">चलन क्र.</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.challanNo}
                                                    onChange={(e) => setFormData({...formData, challanNo: e.target.value})}
                                                    placeholder="चलन क्र. भरा." 
                                                    className="border p-1 rounded w-32" 
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* DEFAULT FORM LAYOUT FOR OTHER TABS */}
                                        <div className="grid grid-cols-4 gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <label>दिनांक</label>
                                                <input 
                                                    type="date" 
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                            <div className="col-span-1 bg-green-600 text-white p-1 px-3 rounded text-center">
                                                {formData.voucherType}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label>चलन क्र.</label>
                                                <input 
                                                    type="text" 
                                                    value={formData.challanNo}
                                                    onChange={(e) => setFormData({...formData, challanNo: e.target.value})}
                                                    placeholder="चलन क्र. भरा" 
                                                    className="border p-1 rounded flex-1" 
                                                />
                                            </div>
                                        </div>

                                        {/* CHEQUE DETAILS */}
                                        {activeTab.includes('cheque') && (
                                            <div className="grid grid-cols-3 gap-4 mb-4 bg-yellow-50 p-2 rounded border border-yellow-200">
                                                <div className="flex items-center gap-2">
                                                    <label>बँकेचे नाव:</label>
                                                    <input 
                                                        type="text" 
                                                        value={formData.bankName || ''}
                                                        onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                                                        placeholder="बँकेचे नाव" 
                                                        className="border p-1 rounded flex-1" 
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label>धनादेश क्र.:</label>
                                                    <input 
                                                        type="text" 
                                                        value={formData.chequeNo || ''}
                                                        onChange={(e) => setFormData({...formData, chequeNo: e.target.value})}
                                                        placeholder="चेक नंबर" 
                                                        className="border p-1 rounded flex-1" 
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label>धनादेश दिनांक:</label>
                                                    <input 
                                                        type="date" 
                                                        value={formData.chequeDate || ''}
                                                        onChange={(e) => setFormData({...formData, chequeDate: e.target.value})}
                                                        className="border p-1 rounded flex-1" 
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-4 mb-4 bg-gray-100 p-2 rounded">
                                            <label className="w-32">व्यक्तीचे नाव :</label>
                                            <div className="flex-1 flex gap-2">
                                                <input 
                                                    type="text" 
                                                    value={formData.personName}
                                                    onChange={(e) => setFormData({...formData, personName: e.target.value})}
                                                    placeholder="व्यक्तीचे नाव" 
                                                    className="border p-2 rounded w-full" 
                                                />
                                                <button 
                                                    onClick={() => setShowPersonModal(true)}
                                                    className="bg-blue-800 text-white px-3 rounded"
                                                >
                                                    ...
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Transaction Entry Row */}
                                <div className="border border-blue-900 mb-4">
                                    <div className="bg-blue-800 text-white grid grid-cols-12 text-sm text-center p-1 font-bold">
                                        <div className="col-span-1 border-r border-blue-700">#</div>
                                        <div className={`${activeTab === 'jv' ? 'col-span-3' : 'col-span-5'} border-r border-blue-700`}>खातावणी निवडा</div>
                                        
                                        {activeTab === 'jv' ? (
                                            <>
                                                <div className="col-span-2 border-r border-blue-700">जमा रक्कम</div>
                                                <div className="col-span-2 border-r border-blue-700">नावे रक्कम</div>
                                                <div className="col-span-2 border-r border-blue-700">वाटणीची रक्कम</div>
                                                <div className="col-span-2">शिल्लक</div>
                                            </>
                                        ) : activeTab === 'cheque-nave' ? (
                                            <>
                                                <div className="col-span-2 border-r border-blue-700">नावे रक्कम</div>
                                                <div className="col-span-2 border-r border-blue-700">वाटणीची रक्कम</div>
                                                <div className="col-span-2">शिल्लक</div>
                                            </>
                                        ) : (
                                            <>
                                               <div className="col-span-2 border-r border-blue-700">रक्कम</div>
                                               <div className="col-span-4">शेरा</div>
                                            </>
                                        )}
                                    </div>

                                    {/* INPUT ROW */}
                                    <div className="p-2 grid grid-cols-12 gap-2 items-center bg-blue-50 border-b">
                                        <div className="col-span-1 text-center">
                                            <button 
                                                onClick={() => setCurrentRow({ account: null, amount: '', creditAmount: '', debitAmount: '', narration: '' })}
                                                className="bg-red-500 text-white rounded w-6 h-6 flex items-center justify-center mx-auto"
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                        <div className={`${activeTab === 'jv' ? 'col-span-3' : 'col-span-5'} flex gap-1`}>
                                            <button 
                                                onClick={() => setShowAccountModal('transaction')}
                                                className="bg-blue-800 text-white px-2 rounded min-w-[30px]"
                                            >
                                                ...
                                            </button>
                                            <input 
                                                readOnly 
                                                value={currentRow.account?.accountName || ''}
                                                placeholder="खाते निवडा"
                                                className="border p-1 flex-1 bg-white rounded" 
                                            />
                                        </div>

                                        {activeTab === 'jv' ? (
                                            <>
                                                <div className="col-span-2">
                                                    <input 
                                                        type="number" 
                                                        value={currentRow.creditAmount}
                                                        onChange={(e) => setCurrentRow({...currentRow, creditAmount: e.target.value})}
                                                        className="border p-1 w-full text-right bg-white rounded" 
                                                        placeholder="0" 
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <input 
                                                        type="number" 
                                                        value={currentRow.debitAmount}
                                                        onChange={(e) => setCurrentRow({...currentRow, debitAmount: e.target.value})}
                                                        className="border p-1 w-full text-right bg-white rounded" 
                                                        placeholder="0" 
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleAddRow();
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <input disabled className="border p-1 w-full bg-gray-200" />
                                                </div>
                                                <div className="col-span-2">
                                                    <input disabled value="0" className="border p-1 w-full bg-gray-200 text-right" />
                                                </div>
                                            </>
                                        ) : activeTab === 'cheque-nave' ? (
                                            <>
                                                <div className="col-span-2">
                                                    <input 
                                                        type="number" 
                                                        value={currentRow.amount}
                                                        onChange={(e) => setCurrentRow({...currentRow, amount: e.target.value})}
                                                        className="border p-1 w-full text-right bg-white rounded" 
                                                        placeholder="0" 
                                                    />
                                                </div>
                                                <div className="col-span-2"><input disabled className="border p-1 w-full bg-gray-200" /></div>
                                                <div className="col-span-2"><input disabled value="0" className="border p-1 w-full bg-gray-200 text-right" /></div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="col-span-2">
                                                    <input 
                                                        type="number" 
                                                        value={currentRow.amount}
                                                        onChange={(e) => setCurrentRow({...currentRow, amount: e.target.value})}
                                                        className="border p-1 w-full text-right bg-white rounded" 
                                                        placeholder="0" 
                                                    />
                                                </div>
                                                <div className="col-span-4">
                                                    <input 
                                                        value={currentRow.narration}
                                                        onChange={(e) => setCurrentRow({...currentRow, narration: e.target.value})}
                                                        className="border p-1 w-full bg-white rounded" 
                                                        placeholder="शेरा"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleAddRow();
                                                        }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    
                                    {/* Shera row specifically for JV (as per screenshot) or just Add button */}
                                    <div className="p-2 border-b flex justify-end">
                                        <button 
                                            onClick={handleAddRow}
                                            className="bg-green-600 text-white px-6 py-1 rounded"
                                        >
                                            Add
                                        </button>
                                    </div>

                                    {/* Added Rows List */}
                                    {formData.transactions.map((tx, idx) => (
                                        <div key={tx.id} className="p-2 grid grid-cols-12 gap-2 items-center border-b text-sm bg-white hover:bg-gray-50">
                                            <div className="col-span-1 text-center">{idx + 1}</div>
                                            <div className={`${activeTab === 'jv' ? 'col-span-3' : 'col-span-5'}`}>{tx.accountName}</div>
                                            
                                            {activeTab === 'jv' ? (
                                                <>
                                                    <div className="col-span-2 text-right">{tx.creditAmount || 0}</div>
                                                    <div className="col-span-2 text-right">{tx.debitAmount || 0}</div>
                                                    <div className="col-span-2"></div>
                                                    <div className="col-span-2"></div>
                                                </>
                                            ) : activeTab === 'cheque-nave' ? (
                                                <>
                                                    <div className="col-span-2 text-right">{tx.amount}</div>
                                                    <div className="col-span-2"></div>
                                                    <div className="col-span-2"></div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="col-span-2 text-right">{tx.amount}</div>
                                                    <div className="col-span-4">{tx.narration}</div>
                                                </>
                                            )}

                                            <div className="col-span-12 flex justify-end">
                                                <button onClick={() => handleRemoveRow(tx.id)} className="text-red-500 text-xs underline">Remove</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Footer TOTAL */}
                                <div className="flex justify-end p-2 bg-gray-100 font-bold mb-4">
                                    Total: {formData.totalAmount}
                                </div>

                                <div className="flex justify-center gap-4 bg-blue-800 p-2 rounded -mx-4 -mb-4">
                                    <button onClick={handleSave} className="bg-green-600 text-white px-6 py-2 rounded">साठविणे</button>
                                    <button onClick={() => setView('list')} className="bg-red-600 text-white px-6 py-2 rounded">रद्द करणे</button>
                                </div>
                            </div>
                        )}

                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500 text-xl font-bold border border-dashed border-gray-400 rounded-lg">
                        Coming Soon: {sidebarItems.find(i => i.id === activeTab)?.label}
                    </div>
                )}
            </div>

            {/* ACCOUNT MODAL */}
            <AccountSelectionModal 
                isOpen={!!showAccountModal}
                onClose={() => setShowAccountModal(false)}
                onSelect={(acc) => {
                    if (showAccountModal === 'bank') {
                        setFormData({ 
                            ...formData, 
                            bankLedgerId: acc.id, 
                            bankLedgerName: acc.accountName 
                        });
                    } else {
                        setCurrentRow({ ...currentRow, account: acc });
                    }
                    setShowAccountModal(false);
                }}
            />

            <ChequeDetailsModal 
                isOpen={showChequeDetailsModal}
                onClose={() => setShowChequeDetailsModal(false)}
                initialData={formData}
                onSave={handleSaveChequeDetails}
            />

            {/* PERSON SELECTION MODALS */}
            <PersonSelectionModal 
                isOpen={showPersonModal}
                onClose={() => setShowPersonModal(false)}
                onSelect={handlePersonSelect}
                onAddNew={() => {
                    setShowPersonModal(false);
                    setShowPersonForm(true);
                }}
            />
            <PersonDetailsForm 
                isOpen={showPersonForm}
                onClose={() => setShowPersonForm(false)}
                onSave={handleSaveNewPerson}
            />

            {/* VIEW TRANSACTION MODAL */}
            <TransactionViewModal 
                isOpen={!!viewingTx}
                onClose={() => setViewingTx(null)}
                transaction={viewingTx}
            />

        </div>
    );
};

export default OtherTransactions;
